// ═══════════════════════════════════════════════════════════════════════════════
// Super admin — list / create / detail (modal) for admin accounts
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import type { AdminUserDetail, AdminUserPublic } from '@fdh/shared';
import { ADMIN_ROLE } from '@fdh/shared';
import { adminApi } from '../../lib/adminApi';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { AdminIpWithGeo } from '../../components/admin/AdminIpWithGeo';

type AdminsPayload = { admins: AdminUserPublic[] };
type TabKey = 'list' | 'create';

export const AdminAdminsPage = () => {
  const { t } = useLanguage();
  const { admin: current } = useAdminAuth();
  const [tab, setTab] = useState<TabKey>('list');
  const [admins, setAdmins] = useState<AdminUserPublic[]>([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState<(typeof ADMIN_ROLE)[keyof typeof ADMIN_ROLE]>(
    ADMIN_ROLE.SUPPORT_ADMIN
  );
  const [submitting, setSubmitting] = useState(false);

  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRole, setDetailRole] = useState<(typeof ADMIN_ROLE)[keyof typeof ADMIN_ROLE]>(
    ADMIN_ROLE.SUPPORT_ADMIN
  );
  const [detailTwoFactor, setDetailTwoFactor] = useState(false);
  const [detailActive, setDetailActive] = useState(true);
  const [detailSaving, setDetailSaving] = useState(false);
  const [operatorPassword, setOperatorPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetBusy, setResetBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await adminApi.get<AdminsPayload>('/admin/admins');
      if (res.success && res.data?.admins) {
        setAdmins(res.data.admins);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = useCallback(async () => {
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      await adminApi.post<AdminUserPublic>('/admin/admins', {
        email: email.trim(),
        password,
        confirmPassword,
        role,
      });
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setMessage(t('admin.adminsCreateSuccess'));
      setTab('list');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }, [email, password, confirmPassword, role, load, t]);

  const handleOpenDetail = useCallback(
    async (adminId: string) => {
      setError('');
      setMessage('');
      setDetailLoading(true);
      setOperatorPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      try {
        const res = await adminApi.get<AdminUserDetail>(
          `/admin/admins/${encodeURIComponent(adminId)}`,
        );
        if (!res.success || !res.data) {
          throw new Error(res.error ?? t('auth.errorGeneric'));
        }
        setDetail(res.data);
        setDetailRole(res.data.role);
        setDetailTwoFactor(res.data.twoFactorEnabled);
        setDetailActive(res.data.isActive);
      } catch (e) {
        setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [t],
  );

  const handleCloseDetail = useCallback(() => {
    setDetail(null);
    setOperatorPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
  }, []);

  const handleSaveDetail = useCallback(async () => {
    if (!detail) {
      return;
    }
    setDetailSaving(true);
    setError('');
    setMessage('');
    try {
      const res = await adminApi.put<AdminUserPublic>(
        `/admin/admins/${encodeURIComponent(detail.id)}`,
        {
          role: detailRole,
          twoFactorEnabled: detailTwoFactor,
          isActive: detailActive,
        },
      );
      if (!res.success || !res.data) {
        throw new Error(res.error ?? t('auth.errorGeneric'));
      }
      setMessage(t('admin.adminsUpdateSuccess'));
      await handleOpenDetail(detail.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    } finally {
      setDetailSaving(false);
    }
  }, [detail, detailRole, detailTwoFactor, detailActive, handleOpenDetail, load, t]);

  const handleResetPassword = useCallback(async () => {
    if (!detail) {
      return;
    }
    setResetBusy(true);
    setError('');
    setMessage('');
    try {
      await adminApi.put(`/admin/admins/${encodeURIComponent(detail.id)}/reset-password`, {
        operatorPassword,
        newPassword,
        confirmNewPassword,
      });
      setOperatorPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setMessage(t('admin.adminsResetPasswordSuccess'));
      await handleOpenDetail(detail.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('auth.errorGeneric'));
    } finally {
      setResetBusy(false);
    }
  }, [detail, operatorPassword, newPassword, confirmNewPassword, handleOpenDetail, t]);

  if (current?.role !== ADMIN_ROLE.SUPER_ADMIN) {
    return (
      <p className="text-sm text-red-400" role="alert">
        {t('admin.adminsForbidden')}
      </p>
    );
  }

  const isSelf = detail?.id === current.id;
  const cannotDeactivateSelf = isSelf;

  return (
    <div>
      <h1 className="text-2xl font-semibold text-white">{t('admin.nav.admins')}</h1>
      <p className="mt-1 text-sm text-slate-400">{t('admin.adminsSubtitle')}</p>

      <div className="mt-6 flex gap-2" role="tablist" aria-label={t('admin.nav.admins')}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'list'}
          className={`rounded-md px-4 py-2 text-sm font-medium ${
            tab === 'list'
              ? 'bg-amber-500 text-slate-950'
              : 'border border-slate-700 text-slate-300 hover:bg-slate-900'
          }`}
          onClick={() => setTab('list')}
        >
          {t('admin.adminsList')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'create'}
          className={`rounded-md px-4 py-2 text-sm font-medium ${
            tab === 'create'
              ? 'bg-amber-500 text-slate-950'
              : 'border border-slate-700 text-slate-300 hover:bg-slate-900'
          }`}
          onClick={() => setTab('create')}
        >
          {t('admin.adminsCreate')}
        </button>
      </div>

      {error ? (
        <p className="mt-4 text-sm text-red-400" role="alert">
          {error}
        </p>
      ) : null}
      {message ? (
        <p className="mt-4 text-sm text-emerald-400" role="status">
          {message}
        </p>
      ) : null}

      {tab === 'create' ? (
        <section className="mt-8 max-w-xl rounded-lg border border-slate-800 bg-slate-900/60 p-4">
          <h2 className="text-lg font-medium text-white">{t('admin.adminsCreate')}</h2>
          <div className="mt-4 space-y-3">
            <input
              type="email"
              value={email}
              onChange={(ev) => setEmail(ev.target.value)}
              placeholder={t('admin.email')}
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              aria-label={t('admin.email')}
            />
            <input
              type="password"
              value={password}
              onChange={(ev) => setPassword(ev.target.value)}
              placeholder={t('admin.password')}
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              aria-label={t('admin.password')}
            />
            <input
              type="password"
              value={confirmPassword}
              onChange={(ev) => setConfirmPassword(ev.target.value)}
              placeholder={t('admin.createConfirmPasswordPlaceholder')}
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              aria-label={t('admin.createConfirmPasswordPlaceholder')}
            />
            <select
              value={role}
              onChange={(ev) =>
                setRole(ev.target.value as (typeof ADMIN_ROLE)[keyof typeof ADMIN_ROLE])
              }
              className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
              aria-label={t('admin.usersColRole')}
            >
              <option value={ADMIN_ROLE.SUPER_ADMIN}>super_admin</option>
              <option value={ADMIN_ROLE.SUPPORT_ADMIN}>support_admin</option>
            </select>
            <button
              type="button"
              disabled={
                submitting ||
                !email.trim() ||
                !password ||
                !confirmPassword ||
                password !== confirmPassword
              }
              className="rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50"
              onClick={() => void handleCreate()}
            >
              {t('admin.adminsCreateSubmit')}
            </button>
          </div>
        </section>
      ) : (
        <section className="mt-8 overflow-x-auto rounded-lg border border-slate-800">
          <table className="min-w-full divide-y divide-slate-800 text-left text-sm">
            <thead className="bg-slate-900/80 text-slate-400">
              <tr>
                <th className="px-3 py-2 font-medium">{t('admin.email')}</th>
                <th className="px-3 py-2 font-medium">{t('admin.usersColRole')}</th>
                <th className="px-3 py-2 font-medium">{t('admin.adminsCol2fa')}</th>
                <th className="px-3 py-2 font-medium">{t('admin.adminsColStatus')}</th>
                <th className="px-3 py-2 font-medium">{t('admin.adminsColActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {admins.map((a) => (
                <tr key={a.id} className="text-slate-300">
                  <td className="px-3 py-2">{a.email}</td>
                  <td className="px-3 py-2">{a.role}</td>
                  <td className="px-3 py-2">
                    {a.twoFactorEnabled ? t('admin.yes') : t('admin.no')}
                  </td>
                  <td className="px-3 py-2">
                    {a.isActive ? t('admin.active') : t('admin.inactive')}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="rounded bg-slate-800 px-3 py-1 text-xs text-amber-300 hover:bg-slate-700"
                      onClick={() => void handleOpenDetail(a.id)}
                      tabIndex={0}
                      aria-label={t('admin.adminsOpenDetail')}
                    >
                      {t('admin.adminsOpenDetail')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {(detail || detailLoading) && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 px-4 py-8"
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-detail-title"
        >
          <div className="w-full max-w-3xl rounded-xl border border-slate-700 bg-slate-950 p-6 shadow-2xl">
            {detailLoading || !detail ? (
              <p className="text-sm text-slate-400" role="status">
                {t('common.loading')}
              </p>
            ) : (
              <>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 id="admin-detail-title" className="text-xl font-semibold text-white">
                      {t('admin.adminsDetailTitle')}
                    </h2>
                    <p className="mt-1 text-sm text-slate-400">{detail.email}</p>
                  </div>
                  <button
                    type="button"
                    className="rounded-md border border-slate-600 px-3 py-1.5 text-sm text-slate-300 hover:bg-slate-900"
                    onClick={handleCloseDetail}
                    aria-label={t('common.close')}
                  >
                    {t('common.close')}
                  </button>
                </div>

                <dl className="mt-6 grid gap-3 sm:grid-cols-2">
                  <div>
                    <dt className="text-xs text-slate-500">{t('admin.adminsCreatedAt')}</dt>
                    <dd className="text-slate-200">{detail.createdAt}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">{t('admin.adminsCreatedBy')}</dt>
                    <dd className="text-slate-200">{detail.createdByEmail ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">{t('admin.summary.lastLoginAt')}</dt>
                    <dd className="text-slate-200">{detail.lastLoginAt ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">{t('admin.summary.lastLoginIp')}</dt>
                    <dd className="text-slate-200">
                      <AdminIpWithGeo ip={detail.lastLoginIp} geo={detail.lastLoginIpGeo} />
                    </dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-xs text-slate-500">{t('admin.summary.lastLoginDevice')}</dt>
                    <dd className="break-all text-slate-200">
                      {detail.lastLoginDevice ?? '—'}
                    </dd>
                  </div>
                </dl>

                <div className="mt-6 space-y-3 rounded-lg border border-slate-800 p-4">
                  <label className="block text-xs text-slate-400">
                    {t('admin.usersColRole')}
                    <select
                      value={detailRole}
                      onChange={(ev) =>
                        setDetailRole(
                          ev.target.value as (typeof ADMIN_ROLE)[keyof typeof ADMIN_ROLE],
                        )
                      }
                      className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white"
                    >
                      <option value={ADMIN_ROLE.SUPER_ADMIN}>super_admin</option>
                      <option value={ADMIN_ROLE.SUPPORT_ADMIN}>support_admin</option>
                    </select>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={detailTwoFactor}
                      onChange={(ev) => setDetailTwoFactor(ev.target.checked)}
                    />
                    {t('admin.adminsEnableEmail2fa')}
                  </label>
                  <p className="text-xs text-slate-500">{t('admin.adminsEnable2faHint')}</p>
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={detailActive}
                      disabled={cannotDeactivateSelf}
                      onChange={(ev) => setDetailActive(ev.target.checked)}
                    />
                    {t('admin.active')}
                  </label>
                  {cannotDeactivateSelf ? (
                    <p className="text-xs text-amber-400/90">{t('admin.adminsCannotDeactivateSelf')}</p>
                  ) : null}
                  <button
                    type="button"
                    disabled={detailSaving}
                    className="rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50"
                    onClick={() => void handleSaveDetail()}
                  >
                    {detailSaving ? t('common.loading') : t('common.save')}
                  </button>
                </div>

                <div className="mt-6 space-y-3 rounded-lg border border-slate-800 p-4">
                  <h3 className="text-sm font-medium text-white">
                    {t('admin.adminsResetPasswordTitle')}
                  </h3>
                  <p className="text-xs text-slate-500">{t('admin.adminsResetPasswordHint')}</p>
                  <input
                    type="password"
                    value={operatorPassword}
                    onChange={(ev) => setOperatorPassword(ev.target.value)}
                    placeholder={t('admin.adminsOperatorPassword')}
                    className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
                    aria-label={t('admin.adminsOperatorPassword')}
                  />
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(ev) => setNewPassword(ev.target.value)}
                    placeholder={t('admin.usersNewPasswordPlaceholder')}
                    className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
                    aria-label={t('admin.usersNewPasswordPlaceholder')}
                  />
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(ev) => setConfirmNewPassword(ev.target.value)}
                    placeholder={t('admin.usersConfirmNewPasswordPlaceholder')}
                    className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm"
                    aria-label={t('admin.usersConfirmNewPasswordPlaceholder')}
                  />
                  <button
                    type="button"
                    disabled={
                      resetBusy ||
                      !operatorPassword ||
                      !newPassword ||
                      !confirmNewPassword ||
                      newPassword !== confirmNewPassword
                    }
                    className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-slate-950 disabled:opacity-50"
                    onClick={() => void handleResetPassword()}
                  >
                    {resetBusy ? t('common.loading') : t('admin.usersResetPassword')}
                  </button>
                </div>

                <section className="mt-6">
                  <h3 className="text-sm font-medium text-white">
                    {t('admin.adminsActivityTitle')}
                  </h3>
                  <p className="mt-1 text-xs text-slate-500">{t('admin.adminsActivityHint')}</p>
                  {detail.recentActivity.length === 0 ? (
                    <p className="mt-3 text-sm text-slate-500">{t('admin.adminsActivityEmpty')}</p>
                  ) : (
                    <ul className="mt-3 max-h-72 space-y-2 overflow-y-auto">
                      {detail.recentActivity.map((item) => (
                        <li
                          key={item.id}
                          className="rounded-md border border-slate-800 px-3 py-2 text-xs text-slate-300"
                        >
                          <div className="font-medium text-slate-200">
                            {item.createdAt} · {item.action}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-x-1 text-slate-500">
                            <span>{item.targetType}</span>
                            {item.targetId ? <span>· {item.targetId}</span> : null}
                            {item.ip ? (
                              <>
                                <span>·</span>
                                <AdminIpWithGeo ip={item.ip} geo={item.ipGeo} />
                              </>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
